import api from './api';

export interface HardwareDevice {
  id: number;
  device_name: string;
  device_ip: string;
  tailscale_ip?: string | null;
  project_id?: string | null;
  camera_count: number;
  status: 'online' | 'offline' | 'unknown' | 'scanning';
  access_control?: any;
  last_seen_at?: string | null;
  device_info?: any;
  created_at: string;
  updated_at: string;
  project?: {
    project_id: string;
    project_name: string;
  };
}

export interface HardwareDeviceListResponse {
  success: boolean;
  data: HardwareDevice[];
}

export interface HardwareDeviceResponse {
  success: boolean;
  data: HardwareDevice;
}

export interface CreateHardwareDeviceRequest {
  device_name: string;
  device_ip: string;
  tailscale_ip?: string;
  project_id?: string;
  camera_count?: number;
  status?: 'online' | 'offline' | 'unknown' | 'scanning';
  access_control?: any;
  device_info?: any;
}

export interface UpdateHardwareDeviceRequest extends Partial<CreateHardwareDeviceRequest> {}

export interface ScanNetworkRequest {
  ip_range?: string;
  use_tailscale?: boolean;
}

export interface ScanNetworkResponse {
  success: boolean;
  data: Array<{
    device_name: string;
    device_ip: string;
    tailscale_ip?: string;
    status: string;
    device_info?: any;
  }>;
  count: number;
}

export interface CheckStatusResponse {
  success: boolean;
  data: {
    device_id: number;
    status: string;
    is_online: boolean;
    checked_at: string;
  };
}

class HardwareService {
  async list(projectId?: string, status?: string): Promise<HardwareDevice[]> {
    const params = new URLSearchParams();
    if (projectId) params.append('project_id', projectId);
    if (status) params.append('status', status);
    
    const query = params.toString();
    const { data } = await api.get<HardwareDeviceListResponse>(`/hardware${query ? `?${query}` : ''}`);
    return data.data || [];
  }

  async getById(id: number): Promise<HardwareDevice> {
    const { data } = await api.get<HardwareDeviceResponse>(`/hardware/${id}`);
    return data.data;
  }

  async create(device: CreateHardwareDeviceRequest): Promise<HardwareDevice> {
    const { data } = await api.post<HardwareDeviceResponse>('/hardware', device);
    if (!data?.success) throw new Error('Nie udało się utworzyć urządzenia');
    return data.data;
  }

  async update(id: number, device: UpdateHardwareDeviceRequest): Promise<HardwareDevice> {
    const { data } = await api.put<HardwareDeviceResponse>(`/hardware/${id}`, device);
    if (!data?.success) throw new Error('Nie udało się zaktualizować urządzenia');
    return data.data;
  }

  async delete(id: number): Promise<void> {
    const { data } = await api.delete<{ success: boolean; message?: string }>(`/hardware/${id}`);
    if (!data?.success) throw new Error(data?.message || 'Nie udało się usunąć urządzenia');
  }

  async scanNetwork(request: ScanNetworkRequest): Promise<ScanNetworkResponse> {
    const { data } = await api.post<ScanNetworkResponse>('/hardware/scan', request);
    return data;
  }

  async checkStatus(id: number): Promise<CheckStatusResponse> {
    const { data } = await api.post<CheckStatusResponse>(`/hardware/${id}/check-status`);
    return data;
  }
}

const hardwareService = new HardwareService();
export default hardwareService;
